import styled from 'styled-components';
import { useListChunksQuery } from '@shared/api';
import { Modal } from '@shared/ui/Modal';
import { Loader } from '@shared/ui/Progress';
import { Badge } from '@shared/ui/Badge';
import type { KnowledgeDocument } from '@entities/knowledge/types';

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
`;

const ChunkCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3.5)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const ChunkMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const ChunkBody = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;

const Section = styled.span`
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Empty = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing(6)};
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
`;

interface Props {
  open: boolean;
  document: KnowledgeDocument | null;
  onClose: () => void;
}

export function DocumentChunksModal({ open, document, onClose }: Props) {
  const { data, isLoading } = useListChunksQuery(
    document ? { docId: document.id, limit: 200 } : ({ docId: 'noop' } as never),
    { skip: !document },
  );

  if (!document) return null;

  return (
    <Modal open={open} title={`Извлечённые требования: ${document.title}`} onClose={onClose}>
      <Header>
        <Badge>{document.document_type.toUpperCase()}</Badge>
        {document.document_number && <span className="mono">{document.document_number}</span>}
        {document.version && <span>v{document.version}</span>}
        {document.total_pages && <span>· {document.total_pages} стр.</span>}
        <span>· {data?.items.length ?? 0} фрагментов сохранено</span>
      </Header>

      <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
        Это всё, что агент извлёк из документа и будет использовать при будущих проверках — каждый
        фрагмент проиндексирован и доступен через семантический поиск.
      </div>

      {isLoading && <Loader />}

      {data && data.items.length === 0 && <Empty>Фрагменты ещё не индексированы.</Empty>}

      {data?.items.map((c) => (
        <ChunkCard key={c.id}>
          <ChunkMeta>
            <span>#{c.chunk_index}</span>
            {c.page_number != null && <span>стр. {c.page_number}</span>}
            {c.section_title && <Section>{c.section_title}</Section>}
            {c.token_count != null && <span>· {c.token_count} токенов</span>}
          </ChunkMeta>
          <ChunkBody>{c.content}</ChunkBody>
        </ChunkCard>
      ))}
    </Modal>
  );
}
